import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2, Users, Activity, CreditCard, Bell } from "lucide-react";

function CrudSection({ title, endpoint, columns, emptyForm, fields, testidPrefix, renderExtra }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get(endpoint); setItems(data); } catch { toast.error("Errore caricamento"); }
  }, [endpoint]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...emptyForm, ...it }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {};
      fields.forEach((f) => {
        let v = form[f.key];
        if (f.type === "number") v = parseFloat(v) || 0;
        payload[f.key] = v;
      });
      if (emptyForm.attivo !== undefined) payload.attivo = form.attivo;
      if (editing) { await api.put(`${endpoint}/${editing.id}`, payload); toast.success("Aggiornato"); }
      else { await api.post(endpoint, payload); toast.success("Creato"); }
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`${endpoint}/${id}`); toast.success("Eliminato"); load(); } catch { toast.error("Errore"); }
  };

  const canEdit = endpoint !== "/payment-methods";

  return (
    <Card className="overflow-hidden border-slate-200">
      <div className="flex items-center justify-between p-4 border-b bg-slate-50">
        <h3 className="font-heading font-semibold text-slate-800">{title}</h3>
        <Button size="sm" onClick={openNew} data-testid={`${testidPrefix}-add`}><Plus className="mr-1.5 h-4 w-4" /> Aggiungi</Button>
      </div>
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            {columns.map((c) => <TableHead key={c.key} className={c.hide}>{c.label}</TableHead>)}
            <TableHead className="text-right">Azioni</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.length === 0 ? (
            <TableRow><TableCell colSpan={columns.length + 1} className="text-center text-sm text-muted-foreground py-8">Nessun elemento</TableCell></TableRow>
          ) : items.map((it) => (
            <TableRow key={it.id} data-testid={`${testidPrefix}-row-${it.id}`} className="hover:bg-slate-50/60">
              {columns.map((c) => <TableCell key={c.key} className={c.hide}>{c.render ? c.render(it) : it[c.key]}</TableCell>)}
              <TableCell className="text-right">
                <div className="flex justify-end gap-1">
                  {canEdit && <Button variant="ghost" size="icon" onClick={() => openEdit(it)}><Pencil className="h-4 w-4" /></Button>}
                  <Button variant="ghost" size="icon" onClick={() => remove(it.id)} className="text-destructive hover:text-destructive" data-testid={`${testidPrefix}-delete-${it.id}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editing ? "Modifica" : "Nuovo"} — {title}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {fields.map((f) => (
                <div key={f.key} className={f.full ? "col-span-2" : ""}>
                  <Label>{f.label}{f.required ? " *" : ""}</Label>
                  <Input type={f.type || "text"} step={f.type === "number" ? "0.01" : undefined} required={f.required}
                         value={form[f.key] ?? ""} onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))}
                         data-testid={`${testidPrefix}-field-${f.key}`} className="mt-1.5" />
                </div>
              ))}
            </div>
            {emptyForm.attivo !== undefined && (
              <div className="flex items-center justify-between rounded-lg border p-3">
                <Label>Attivo</Label>
                <Switch checked={form.attivo} onCheckedChange={(v) => setForm((s) => ({ ...s, attivo: v }))} />
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
              <Button type="submit" disabled={saving} data-testid={`${testidPrefix}-save`}>{saving ? "..." : "Salva"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

const colorDot = (it) => (
  <span className="inline-flex items-center gap-2">
    <span className="h-3 w-3 rounded-full" style={{ backgroundColor: it.colore }} />
    {it.nome}
  </span>
);
const activeBadge = (it) => (
  <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${it.attivo ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
    {it.attivo ? "Attivo" : "Non attivo"}
  </span>
);

function ReminderSettings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [waOk, setWaOk] = useState(false);

  useEffect(() => {
    api.get("/settings/whatsapp-status").then(({ data }) => setWaOk(data.configured)).catch(() => {});
    api.get("/settings/reminder").then(({ data }) => setS(data)).catch(() => toast.error("Errore caricamento impostazioni"));
  }, []);

  if (!s) return null;

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings/reminder", {
        enabled: s.enabled,
        lead_value: parseInt(s.lead_value) || 1,
        lead_unit: s.lead_unit,
        notify_cliente: s.notify_cliente,
        notify_collaboratore: s.notify_collaboratore,
        whatsapp_enabled: !!s.whatsapp_enabled,
      });
      setS(data);
      toast.success("Impostazioni promemoria salvate");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Card className="border-slate-200 p-5 max-w-xl space-y-5" data-testid="reminder-settings-card">
      <div className="flex items-start gap-3">
        <div className="rounded-lg bg-primary/10 p-2"><Bell className="h-5 w-5 text-primary" /></div>
        <div>
          <h3 className="font-heading font-semibold text-slate-800">Promemoria appuntamenti</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Email automatica inviata a paziente e collaboratore prima di ogni appuntamento.</p>
        </div>
      </div>

      <div className="flex items-center justify-between rounded-lg border p-3">
        <div><Label>Promemoria automatici</Label><p className="text-xs text-muted-foreground mt-0.5">Attiva per tutti gli appuntamenti futuri</p></div>
        <Switch checked={s.enabled} onCheckedChange={(v) => setS((p) => ({ ...p, enabled: v }))} data-testid="reminder-enabled-switch" />
      </div>

      <div>
        <Label>Invia il promemoria</Label>
        <div className="mt-1.5 flex items-center gap-2">
          <Input type="number" min="1" value={s.lead_value} onChange={(e) => setS((p) => ({ ...p, lead_value: e.target.value }))} data-testid="reminder-lead-value" className="w-24" />
          <Select value={s.lead_unit} onValueChange={(v) => setS((p) => ({ ...p, lead_unit: v }))}>
            <SelectTrigger className="w-40" data-testid="reminder-lead-unit"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ore">ore prima</SelectItem>
              <SelectItem value="giorni">giorni prima</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <p className="text-xs text-muted-foreground mt-1.5">Es. "1 giorni prima" = il promemoria parte 24 ore prima dell'orario di inizio.</p>
      </div>

      <div className="space-y-2">
        <Label>Destinatari</Label>
        <div className="flex items-center justify-between rounded-lg border p-3">
          <span className="text-sm">Paziente</span>
          <Switch checked={s.notify_cliente} onCheckedChange={(v) => setS((p) => ({ ...p, notify_cliente: v }))} data-testid="reminder-notify-cliente" />
        </div>
        <div className="flex items-center justify-between rounded-lg border p-3">
          <span className="text-sm">Collaboratore assegnato</span>
          <Switch checked={s.notify_collaboratore} onCheckedChange={(v) => setS((p) => ({ ...p, notify_collaboratore: v }))} data-testid="reminder-notify-collaboratore" />
        </div>
        <p className="text-xs text-muted-foreground">L'email parte ai destinatari con un'email valida in anagrafica; WhatsApp (se attivo) a chi ha un numero di telefono.</p>
      </div>

      <div className="space-y-2">
        <Label>Canali</Label>
        <div className="flex items-center justify-between gap-3 rounded-lg border p-3">
          <div>
            <span className="text-sm">Invia anche su WhatsApp</span>
            <p className="text-xs text-muted-foreground mt-0.5">Al numero di telefono in anagrafica (paziente e collaboratore)</p>
            <Badge variant="outline" className={`mt-1.5 ${waOk ? "border-emerald-300 text-emerald-700" : "border-amber-300 text-amber-700"}`} data-testid="whatsapp-status-badge">
              {waOk ? "WhatsApp collegato" : "Credenziali WhatsApp non ancora configurate: nessun messaggio verrà inviato"}
            </Badge>
          </div>
          <Switch checked={!!s.whatsapp_enabled} onCheckedChange={(v) => setS((p) => ({ ...p, whatsapp_enabled: v }))} data-testid="reminder-whatsapp-switch" />
        </div>
      </div>

      <Button onClick={save} disabled={saving} data-testid="reminder-save">{saving ? "Salvataggio..." : "Salva impostazioni"}</Button>
    </Card>
  );
}

export default function Gestione() {
  return (
    <div className="space-y-6">
      <PageHeader title="Gestione" subtitle="Configura collaboratori, servizi e metodi di pagamento" />
      <Tabs defaultValue="collaboratori">
        <TabsList className="mb-4 h-auto max-w-full flex-wrap justify-start">
          <TabsTrigger value="collaboratori" data-testid="tab-collaboratori"><Users className="mr-1.5 h-4 w-4" /> Collaboratori</TabsTrigger>
          <TabsTrigger value="servizi" data-testid="tab-servizi"><Activity className="mr-1.5 h-4 w-4" /> Servizi & Listino</TabsTrigger>
          <TabsTrigger value="pagamenti" data-testid="tab-pagamenti"><CreditCard className="mr-1.5 h-4 w-4" /> Metodi Pagamento</TabsTrigger>
          <TabsTrigger value="promemoria" data-testid="tab-promemoria"><Bell className="mr-1.5 h-4 w-4" /> Promemoria</TabsTrigger>
        </TabsList>

        <TabsContent value="collaboratori">
          <CrudSection
            title="Collaboratori" endpoint="/collaborators" testidPrefix="collab"
            emptyForm={{ nome: "", cognome: "", team: "", email: "", telefono: "", ruolo: "Fisioterapista", costo_orario: 0, colore: "#17B8B0", attivo: true }}
            fields={[
              { key: "nome", label: "Nome", required: true },
              { key: "cognome", label: "Cognome" },
              { key: "team", label: "Team", full: true },
              { key: "ruolo", label: "Specializzazione" },
              { key: "telefono", label: "Telefono" },
              { key: "email", label: "Email", type: "email", full: true },
              { key: "costo_orario", label: "Costo orario PT-Med €", type: "number" },
              { key: "colore", label: "Colore", type: "color" },
            ]}
            columns={[
              { key: "nome", label: "Nome", render: (it) => <span className="font-medium">{it.nome} {it.cognome}</span> },
              { key: "team", label: "Team", hide: "hidden md:table-cell", render: (it) => it.team ? <Badge variant="secondary">{it.team}</Badge> : "—" },
              { key: "ruolo", label: "Specializzazione", hide: "hidden lg:table-cell" },
              { key: "costo_orario", label: "Costo PT-Med", hide: "hidden lg:table-cell", render: (it) => `€ ${it.costo_orario}` },
              { key: "attivo", label: "Stato", render: activeBadge },
            ]}
          />
        </TabsContent>

        <TabsContent value="servizi">
          <CrudSection
            title="Servizi & Listino" endpoint="/services" testidPrefix="srv"
            emptyForm={{ nome: "", descrizione: "", durata_min: 60, prezzo: 0, colore: "#3B82F6", attivo: true }}
            fields={[
              { key: "nome", label: "Nome servizio", required: true, full: true },
              { key: "durata_min", label: "Durata (min)", type: "number" },
              { key: "prezzo", label: "Prezzo (€)", type: "number" },
              { key: "colore", label: "Colore", type: "color" },
              { key: "descrizione", label: "Descrizione", full: true },
            ]}
            columns={[
              { key: "nome", label: "Servizio", render: colorDot },
              { key: "durata_min", label: "Durata", hide: "hidden md:table-cell", render: (it) => `${it.durata_min} min` },
              { key: "prezzo", label: "Prezzo", render: (it) => <span className="font-mono-data">€ {it.prezzo}</span> },
              { key: "attivo", label: "Stato", hide: "hidden lg:table-cell", render: activeBadge },
            ]}
          />
        </TabsContent>

        <TabsContent value="pagamenti">
          <CrudSection
            title="Metodi di Pagamento" endpoint="/payment-methods" testidPrefix="pay"
            emptyForm={{ nome: "", attivo: true }}
            fields={[{ key: "nome", label: "Nome metodo", required: true, full: true }]}
            columns={[
              { key: "nome", label: "Metodo", render: (it) => <span className="font-medium">{it.nome}</span> },
              { key: "attivo", label: "Stato", render: activeBadge },
            ]}
          />
          <p className="mt-3 text-xs text-muted-foreground">
            Modalità di pagamento disponibili: <strong>Unica Soluzione</strong> e <strong>Singolo Servizio</strong> (predefinite).
          </p>
        </TabsContent>

        <TabsContent value="promemoria">
          <ReminderSettings />
        </TabsContent>
      </Tabs>
    </div>
  );
}
